"use client"

import {useEffect, useState} from "react"
import {languages, useI18n} from "@/lib/i18n"
import {Button} from "@/components/ui/button"
import {Check, ChevronDown, Loader2} from "lucide-react"
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from "@/components/ui/dropdown-menu"
import {useTheme} from "next-themes"
import ReactCountryFlag from "react-country-flag"

// Language display names (for dropdown menu)
const languageNames: Record<string, string> = {
  en: "English",
  de: "Deutsch",
  fr: "Français",
  fa: "فارسی",
  es: "Español",
}

// Language flags (emoji)
const languageFlags: Record<string, string> = {
  en: "🇬🇧",
  de: "🇩🇪",
  fr: "🇫🇷",
  fa: "🇮🇷",
  es: "🇪🇸",
}

// Language codes (abbreviations)
const languageCodes: Record<string, string> = {
  en: "EN",
  de: "DE",
  fr: "FR",
  fa: "FA",
  es: "ES",
}

// Country codes for flags (mapping language codes to country codes)
const countryCodes: Record<string, string> = {
  en: "GB", // English -> United Kingdom
  de: "DE", // German -> Germany
  fr: "FR", // French -> France
  fa: "IR", // Persian -> Iran
  es: "ES", // Spanish -> Spain
}

// Flag component using react-country-flag library with emoji fallback
const LanguageFlag = ({ langCode, emoji }: { langCode: string; emoji: string }) => {
  const countryCode = countryCodes[langCode] || langCode.toUpperCase()
  
  try {
    return (
      <ReactCountryFlag
        countryCode={countryCode}
        svg
        style={{
          width: '1rem',
          height: '0.75rem',
          borderRadius: '0.125rem',
        }}
        className="flex-shrink-0 object-cover rounded-sm"
      />
    )
  } catch (error) {
    // Fallback to emoji if library fails
    return <span className="text-base leading-none">{emoji}</span>
  }
}

export default function LanguageSwitcher() {
  const { language, setLanguage, isLoading } = useI18n()
  const { theme } = useTheme()
  const [isOpen, setIsOpen] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  // Prevent hydration mismatch by not rendering until mounted
  if (!mounted) {
    return (
      <Button variant="outline" size="sm" className="h-8 w-8 p-0">
        <span className="text-base">🌐</span>
      </Button>
    )
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0 hover:bg-accent hover:text-accent-foreground transition-all duration-200 hover:scale-105 active:scale-95"
          aria-label="Select language"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <LanguageFlag langCode={language} emoji={languageFlags[language]} />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang}
            className={`flex items-center gap-2 cursor-pointer hover:bg-accent hover:text-accent-foreground transition-colors ${
              lang === language ? "bg-accent text-accent-foreground" : ""
            }`}
            onClick={() => {
              setLanguage(lang)
              setIsOpen(false)
            }}
          >
            <LanguageFlag langCode={lang} emoji={languageFlags[lang]} />
            <span className="flex-1">{languageNames[lang]}</span>
            {lang === language && <Check className="h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
